import type { RouteLocationNormalizedLoaded } from 'vue-router';

export type MobileScreen = 'activity' | 'conversation' | 'resume' | 'contact';

type MobileRoute = Pick<RouteLocationNormalizedLoaded, 'params' | 'path' | 'query'>;

function singleValue(value: unknown): string | undefined {
  const single = Array.isArray(value) ? value[0] : value;
  return typeof single === 'string' && single ? single : undefined;
}

// Maps the shared jobs route state to a mobile screen. Mobile keeps only
// Conversation/Resume/Contact; other tabs fall through to Conversation.
// Kept in its own module so both MobileConversationsView and the detail tab bar
// share one source of truth without a circular import between them.
export function getMobileScreen(route: MobileRoute): MobileScreen {
  if (route.params.conversationId) {
    // Trim trailing slashes so '/resume/' still matches the tab segment.
    const path = route.path.replace(/\/+$/, '');
    if (path.endsWith('/resume')) {
      return 'resume';
    }
    if (path.endsWith('/contact')) {
      return 'contact';
    }
    return 'conversation';
  }
  // A `?conversationId=` query means the shared jobs router is still resolving a
  // deep link to its canonical candidates path. Map it to the target detail screen
  // right away so the view can show skeletons instead of a frozen Activity list.
  if (getPendingConversationId(route)) {
    const tab = singleValue(route.query.tab);
    return tab === 'resume' || tab === 'contact' ? tab : 'conversation';
  }
  return 'activity';
}

// The conversation id from the query while a deep link resolves; undefined once the
// router has rewritten the URL to the candidates path (or when there is no deep link).
export function getPendingConversationId(
  route: Pick<RouteLocationNormalizedLoaded, 'params' | 'query'>,
): string | undefined {
  if (route.params.conversationId) {
    return undefined;
  }
  return singleValue(route.query.conversationId);
}
